/**
 * Builds the pipeline_config.apprise_vm payload (contract v2 §2) from the wizard form
 * state. The payload is exactly what validation checks, what the review shows and what
 * is sent: every key present, strings trimmed, values that do not apply to the chosen
 * modes blanked out, catalog-locked values replaced by the catalog.
 *
 * The payload never contains a password, private key or any other secret: a custom
 * password travels in the separate write-only top-level `admin_password` field.
 */
import type { AppriseVmConfig, AppriseVmOptions, VmSizing } from '@/types'
import { appriseRegionSupportsZones, deriveHostname, resourceGroupExists } from './appriseDefaults'
import { tagRowsToRecord, tagRecordToRows } from './appriseTags'

export interface ApprisePayloadContext {
  region: string
  options: Pick<AppriseVmOptions, 'availability_zone_regions'>
  catalog: VmSizing
  /** Names of the existing resource groups; decides resource_group_mode (undefined: only the default group is known). */
  resourceGroups?: string[]
}

const trim = (s: string) => s.trim()
const nonBlank = (items: string[]) => items.map(trim).filter(Boolean)

export function buildAppriseVmPayload(config: AppriseVmConfig, ctx: ApprisePayloadContext): AppriseVmConfig {
  const s = config.server
  const n = config.network
  const st = config.storage
  const sec = config.security
  const o = config.operations
  const locked = !ctx.catalog.overrides_allowed
  const existing = s.deployment_mode === 'use_existing'
  const windows = s.os_type === 'windows'
  const bp = st.backup_policy
  const av = s.availability
  const zoneOk = av.type === 'zone' && appriseRegionSupportsZones(ctx.region, ctx.options)
  const trusted = sec.security_type === 'trusted_launch'
  const serverName = trim(s.server_name)
  const publicIp = n.public_ip
  const boot = o.boot_diagnostics

  return {
    sku: config.sku,
    server: {
      deployment_mode: s.deployment_mode,
      server_name: serverName,
      hostname: deriveHostname(serverName, s.os_type), // never typed — follows the VM name
      resource_group_mode: existing || resourceGroupExists(s.resource_group_name, ctx.resourceGroups) ? 'existing' : 'create_new',
      resource_group_name: trim(s.resource_group_name),
      os_type: s.os_type,
      image: {
        publisher: trim(s.image.publisher), offer: trim(s.image.offer), sku: trim(s.image.sku), version: trim(s.image.version) || 'latest',
      },
      vm_size: locked ? ctx.catalog.vm_size : trim(s.vm_size),
      availability: {
        type: av.type === 'zone' && !zoneOk ? 'none' : av.type,
        zone: zoneOk ? av.zone : '',
        availability_set_id: av.type === 'availability_set' ? trim(av.availability_set_id) : '',
        vmss_id: av.type === 'vmss' ? trim(av.vmss_id) : '',
      },
      server_environment: s.server_environment,
      time_zone: windows ? trim(s.time_zone) : '',
      admin_username: trim(s.admin_username),
      admin_access_method: s.admin_access_method,
      admin_source_cidrs: s.admin_access_method === 'private_network' ? nonBlank(s.admin_source_cidrs) : [],
      aad_login_enabled: s.aad_login_enabled,
      rbac_admin_principal_ids: nonBlank(s.rbac_admin_principal_ids),
      // Azure Hybrid Benefit is Windows only; the preference is kept while the user flips images.
      license_type: windows ? s.license_type : 'none',
      license_confirmed: windows && s.license_type === 'windows_server' ? s.license_confirmed : false,
    },
    network: {
      vnet_mode: n.vnet_mode,
      vnet_name: trim(n.vnet_name),
      vnet_address_space: n.vnet_mode === 'create_new' ? trim(n.vnet_address_space) : '',
      vnet_resource_group: n.vnet_mode === 'existing' ? trim(n.vnet_resource_group) : '',
      subnet_mode: n.subnet_mode,
      subnet_name: trim(n.subnet_name),
      subnet_prefix: n.subnet_mode === 'create_new' ? trim(n.subnet_prefix) : '',
      private_ip_allocation: n.private_ip_allocation,
      private_ip_address: n.private_ip_allocation === 'static' ? trim(n.private_ip_address) : '',
      public_ip: {
        mode: publicIp.mode,
        name: publicIp.mode === 'none' ? '' : trim(publicIp.name),
        resource_group: publicIp.mode === 'existing' ? trim(publicIp.resource_group) : '',
      },
      nsg_mode: n.nsg_mode,
      nsg_name: n.nsg_mode === 'none' ? '' : trim(n.nsg_name),
      nsg_resource_group: n.nsg_mode === 'existing' ? trim(n.nsg_resource_group) : '',
      inbound_rules: n.inbound_rules.map((r) => ({
        name: trim(r.name), priority: r.priority, protocol: r.protocol, port: trim(r.port),
        source_cidr: trim(r.source_cidr), description: trim(r.description),
      })),
      outbound_mode: n.outbound_mode,
      dns_servers: nonBlank(n.dns_servers),
      dns_label: publicIp.mode === 'create_new' ? trim(n.dns_label) : '',
      load_balancer: {
        mode: n.load_balancer.mode,
        backend_pool_id: n.load_balancer.mode === 'existing' ? trim(n.load_balancer.backend_pool_id) : '',
      },
    },
    storage: {
      os_disk_type: locked ? ctx.catalog.os_disk_type : st.os_disk_type,
      os_disk_gb: locked ? ctx.catalog.os_disk_gb : st.os_disk_gb,
      data_disk_required: st.data_disk_required,
      data_disks: st.data_disk_required ? st.data_disks.map((d) => ({ size_gb: d.size_gb, type: d.type, caching: d.caching })) : [],
      encryption_at_host: st.encryption_at_host,
      disk_encryption_set_id: trim(st.disk_encryption_set_id),
      backup_required: st.backup_required,
      backup_policy: {
        frequency: bp.frequency,
        time: trim(bp.time),
        retention_days: bp.retention_days,
        vault_mode: bp.vault_mode,
        vault_name: st.backup_required ? trim(bp.vault_name) : '',
        vault_resource_group: st.backup_required && bp.vault_mode === 'existing' ? trim(bp.vault_resource_group) : '',
      },
    },
    security: {
      security_type: sec.security_type,
      identity_mode: locked ? ctx.catalog.identity_default : sec.identity_mode,
      user_assigned_identity_id: sec.identity_mode === 'user_assigned' ? trim(sec.user_assigned_identity_id) : '',
      key_vault_mode: sec.key_vault_mode,
      key_vault_name: sec.key_vault_mode === 'none' ? '' : trim(sec.key_vault_name),
      key_vault_resource_group: sec.key_vault_mode === 'existing' ? trim(sec.key_vault_resource_group) : '',
      credential_mode: sec.credential_mode,
      credential_secret_name: sec.credential_mode === 'existing_key_vault_secret' ? trim(sec.credential_secret_name) : '',
      trusted_launch: trusted,
      secure_boot: trusted && sec.secure_boot,
      vtpm: trusted && sec.vtpm,
      defender_monitoring: sec.defender_monitoring,
      jit_requested: s.admin_access_method === 'jit' && sec.jit_requested,
    },
    operations: {
      monitoring_enabled: locked ? ctx.catalog.monitoring_default : o.monitoring_enabled,
      log_analytics_workspace_id: o.monitoring_enabled ? trim(o.log_analytics_workspace_id) : '',
      boot_diagnostics: {
        mode: boot.mode,
        storage_account: boot.mode === 'custom' ? trim(boot.storage_account) : '',
        storage_account_resource_group: boot.mode === 'custom' ? trim(boot.storage_account_resource_group) : '',
      },
      patch_mode: o.patch_mode,
      periodic_assessment: o.periodic_assessment,
      recommended_alerts: o.recommended_alerts,
      os_guest_diagnostics: o.os_guest_diagnostics,
      app_health_monitoring: o.app_health_monitoring,
      tags: tagRowsToRecord(tagRecordToRows(o.tags)),
    },
  }
}
